import React from "react";
import {Page, Document, StyleSheet, Font, View, Text} from "@react-pdf/renderer";
import OpenSansRegular from "../../../../../statics/typos/openSansRegular.ttf";
import OpenSansSemiBold from "../../../../../statics/typos/openSansSemiBold.ttf";
import OpenSansBold from "../../../../../statics/typos/openSansBold.ttf";
import {OrderFilesUtils} from "../../../../../helpers/orderFilesUtils";
import {ITcrStore} from "../../../../stores/tcrStore";
import {Utils} from "../../../../../helpers/utils";
import {Direction} from "../../../../models/transport";
import TcrUtils from "../../../../../helpers/tcrUtils";
import EquipmentAndDamagesLegend from "./equipmentAndDamagesLegend";
import Front from "./trailer/front";
import Left from "./trailer/left";
import Right from "./trailer/right";
import Rear from "./trailer/rear";

Font.register({
    family: "OpenSans",
    format: "truetype",
    src: OpenSansRegular
});
Font.register({
    family: "OpenSansSemiBold",
    format: "truetype",
    src: OpenSansSemiBold
});
Font.register({
    family: "OpenSansBold",
    format: "truetype",
    src: OpenSansBold
});

const styles = StyleSheet.create(OrderFilesUtils.styles);
const equipmentAndDamageStyles = StyleSheet.create(OrderFilesUtils.equipmentAndDamageStyles);
const tcrReportStyles = StyleSheet.create(OrderFilesUtils.tcrReportStyles);

interface TcrReportFileProps {
    tcrStore: ITcrStore | null | undefined;
    isExport: boolean;
}

export default function TcrReportFile(props: TcrReportFileProps): React.ReactElement{
    if (!props.tcrStore) {
        return <Document><Page/></Document>;
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View>
                    {OrderFilesUtils.renderHeader("TRAILER CONDITION REPORT \n " +
                        "Raport o stanie naczepy")}
                    {renderGeneralInformationTable()}
                    {renderTrailerPhotoSection()}
                    {renderEquipmentAndDamagesTable()}
                    {renderOtherInformation()}
                </View>
            </Page>
        </Document>
    );

    function renderGeneralInformationTable() {
        return (
            <View style={styles.table}>
                <View style={styles.tableRow}>
                    <View style={[styles.tableCol, {minHeight: 20}]}>
                        <Text style={styles.tableCellHeader}>
                            Nr naczepy /{"\n"}
                            Trailer no
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.tcrStore!.trailerRegNo}
                        </Text>
                    </View>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            Właściciel /{"\n"}
                            Owner
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.tcrStore!.transportCompany}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={[styles.tableCol, {minHeight: 20}]}>
                        <Text style={styles.tableCellHeader}>
                            Port załadunku /{"\n"}
                            Port of loading
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.tcrStore!.portOfLoading}
                        </Text>
                    </View>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            Port przeznaczenia /{"\n"}
                            Destination port
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.tcrStore!.destinationPort}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={[styles.tableCol, {minHeight: 20}]}>
                        <Text style={styles.tableCellHeader}>
                            Czas odejścia /{"\n"}
                            Departure time
                        </Text>
                        <Text style={styles.tableCellData}>
                            {Utils.renderTime(props.tcrStore!.arrivalDepartureTime)}
                        </Text>
                    </View>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            Prom /{"\n"}
                            Ferry
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.tcrStore!.ferryName}
                        </Text>
                    </View>
                </View>
            </View>
        );
    }

    function renderOtherInformation() {
        return(
            <View>
                <View style={[tcrReportStyles.row, {flex: "1"}]}>
                    <View style={tcrReportStyles.column}>
                        <Text style={tcrReportStyles.smallSquare} wrap={false}>
                            <Text style={tcrReportStyles.checked}>
                                {Utils.renderBooleanValueInFile(props.tcrStore!.isDangerousGoods)}
                            </Text>
                        </Text>
                        <Text style={tcrReportStyles.header}>
                            Towary niebezpieczne/Dangerous Goods
                        </Text>
                    </View>
                    <View style={[tcrReportStyles.column, {marginLeft: -38}]}>
                        <View style={tcrReportStyles.smallSquare} wrap={false} />
                        <Text style={tcrReportStyles.header}>
                            Izoterma/Isothermal unit
                        </Text>
                    </View>
                    <View style={tcrReportStyles.column}>
                        <View style={tcrReportStyles.smallSquare} wrap={false} />
                        <Text style={tcrReportStyles.header}>
                            Chłodnia/Refregerating Unit
                        </Text>
                    </View>
                </View>
                <View style={[tcrReportStyles.row, {width: "100%"}]}>
                    <View style={[tcrReportStyles.column, {width: "40%"}]}>
                        <View style={tcrReportStyles.smallSquare} wrap={false} />
                        <Text style={tcrReportStyles.header}>
                            Ładunek głęboko mrożony/DeepFrozen Cargo
                        </Text>
                    </View>
                    <View style={[tcrReportStyles.column, {width: "60%"}]}>
                        <View style={tcrReportStyles.smallSquare} wrap={false} />
                        <Text style={tcrReportStyles.header}>
                            Podłączenie do sieci statkowej w czasie rejsu/To be plugged in while at sea
                        </Text>
                    </View>
                </View>
                <View style={[tcrReportStyles.row, {
                    alignItems: "center",
                    justifyContent: "flex-start",
                    marginTop: 13
                }]}>
                    <View style={tcrReportStyles.smallSquare} wrap={false} />
                    <Text style={tcrReportStyles.header}>
                        Nadawca towaru deklaruje, że ładunek znajdujący się na podjeździe został rozmieszczony i zamocowany prawidłowo
                    </Text>
                </View>
                <View style={[tcrReportStyles.row, {
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    marginLeft: 18,
                    marginTop: 13
                }]}>
                    <Text style={tcrReportStyles.header}>
                        Żądana temperatura pracy Komora 1
                    </Text>
                    <Text style={[tcrReportStyles.header, {
                        marginRight: 5,
                        fontFamily: "OpenSansBold"
                    }]}>
                        {Utils.renderValueWithUnit(props.tcrStore!.requestedTemp1, "°C")}
                    </Text>
                    <Text style={[tcrReportStyles.header, {marginLeft: 0}]}>
                        Komora 2
                    </Text>
                    <Text style={[tcrReportStyles.header, {fontFamily: "OpenSansBold"}]}>
                        {Utils.renderValueWithUnit(props.tcrStore!.requestedTemp2, "°C")}
                    </Text>
                </View>
            </View>
        );
    }

    function renderEquipmentAndDamagesTable() {
        return (
            <View style={[styles.table, {marginTop: 20, borderTop: 0, borderLeft: 0}]}>
                <View style={[styles.tableRow, {borderTop: 1, borderLeft: 1}]} fixed>
                    <View style={[styles.tableSmallCol, equipmentAndDamageStyles.tableHeaderColumn, {width: "10%"}]}/>
                    <View style={[styles.tableSmallCol, equipmentAndDamageStyles.tableHeaderColumn, {width: "30%"}]}>
                        <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText]}>
                            Uszkodzenia /{"\n"}
                            Damages
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol, equipmentAndDamageStyles.tableHeaderColumn, {width: "15%"}]}>
                        <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText]}>
                            Wyposażenie /{"\n"}
                            Equipment
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol, equipmentAndDamageStyles.tableHeaderColumn, {width: "7%"}]}>
                        <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText, {marginLeft: 3}]}>
                            Temp
                        </Text>
                    </View>
                    {renderSignColumn()}
                    {renderSignColumn()}
                </View>
                <View style={styles.tableRow}>
                    <View style={[styles.tableSmallCol, {width: "10%", borderLeft: 1}]}>
                        <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText]}>
                            {"Truck>Port"}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol, {width: "30%"}]}>
                        {props.isExport && equipmentAndDamagesTable(Direction.IN)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "15%"}]}>
                        {props.isExport && renderAdditionalEquipmentData(props.tcrStore!.direction === Direction.IN)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "7%"}]}>
                        {props.isExport && renderActualTemperatureData(props.tcrStore!.direction === Direction.IN)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                </View>
                <View style={styles.tableRow}>
                    <View style={[styles.tableSmallCol, {width: "10%", borderLeft: 1}]} break={props.isExport}>
                        <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText]}>
                            {"Port>Statek"}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol, {width: "30%"}]}>
                        {props.isExport && equipmentAndDamagesTable(Direction.OUT)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "15%"}]}>
                        {props.isExport && renderAdditionalEquipmentData(props.tcrStore!.direction === Direction.OUT)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "7%"}]}>
                        {props.isExport && renderActualTemperatureData(props.tcrStore!.direction === Direction.OUT)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                </View>
                <View style={styles.tableRow}>
                    <View style={[styles.tableSmallCol, {width: "10%", borderLeft: 1}]} break={!props.isExport}>
                        <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText]}>
                            {"Statek>Port"}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol, {width: "30%"}]}>
                        {!props.isExport && equipmentAndDamagesTable(Direction.IN)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "15%"}]}>
                        {!props.isExport && renderAdditionalEquipmentData(props.tcrStore!.direction === Direction.IN)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "7%"}]}>
                        {!props.isExport && renderActualTemperatureData(props.tcrStore!.direction === Direction.IN)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                </View>
                <View style={styles.tableRow}>
                    <View style={[styles.tableSmallCol, {width: "10%", borderLeft: 1}]}>
                        <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText]}>
                            {"Port>Truck"}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol, {width: "30%"}]}>
                        {!props.isExport && equipmentAndDamagesTable(Direction.OUT)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "15%"}]}>
                        {!props.isExport && renderAdditionalEquipmentData(props.tcrStore!.direction === Direction.OUT)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "7%"}]}>
                        {!props.isExport && renderActualTemperatureData(props.tcrStore!.direction === Direction.OUT)}
                    </View>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                    <View style={[styles.tableSmallCol, {width: "19%"}]}/>
                </View>
            </View>
        );
    }

    function renderTrailerPhotoSection() {
        return (
            <View style={[{
                display: "flex",
                flexDirection: "row",
                marginTop: 20
            }]}>
                {renderTrailerPhoto()}
                <EquipmentAndDamagesLegend />
            </View>
        );
    }

    function renderTrailerPhoto() {
        return <View style={[{width: "52%"}]}>
            <View style={tcrReportStyles.trailerPhotoRow}>
                <View style={tcrReportStyles.bigSquare} wrap={false}>
                    <Front />
                </View>
                <View style={tcrReportStyles.bigSquare} wrap={false}>
                    <Right />
                </View>
            </View>
            <View style={tcrReportStyles.trailerPhotoRow}>
                <View style={tcrReportStyles.bigSquare} wrap={false}>
                    <Rear />
                </View>
                <View style={tcrReportStyles.bigSquare} wrap={false}>
                    <Left />
                </View>
            </View>
        </View>;
    }

    function renderSignColumn() {
        return (
            <View style={[styles.tableSmallCol, equipmentAndDamageStyles.tableHeaderColumn,  {width: "19%"}]}>
                <Text style={[styles.tableCellHeader, equipmentAndDamageStyles.tableHeaderText]}>
                    Podpis /{"\n"}
                    Sign
                </Text>
            </View>
        );
    }

    function renderAdditionalEquipmentData(isInDirection: boolean) {
        return (
            <View style={equipmentAndDamageStyles.textCenterInCell}>
                <Text style={[styles.tableCellData, equipmentAndDamageStyles.tableCellText]}>
                    {`Korba/ Winch: ${Utils.renderBooleanYesNoValue(isInDirection ? props.tcrStore!.entryWinch.value : props.tcrStore!.exitWinch.value, true)}`}
                </Text>
                <Text style={[styles.tableCellData, equipmentAndDamageStyles.tableCellText]}>
                    {`Plomba/ Plumb: ${Utils.renderBooleanYesNoValue(isInDirection ? props.tcrStore!.entryPlumb.value : props.tcrStore!.exitPlumb.value, true)}`}
                </Text>
                <Text style={[styles.tableCellData, equipmentAndDamageStyles.tableCellText]}>
                    {`Koła zapasowe/ Spare wheels no: ${Utils.convertNullAndUndefinedToEmptyString(isInDirection ? props.tcrStore!.entrySpareWheelsNo.value : props.tcrStore!.exitSpareWheelsNo.value)}`}
                </Text>
                <Text style={[styles.tableCellData, equipmentAndDamageStyles.tableCellText]}>
                    {`Kliny/ Wedges no: ${Utils.convertNullAndUndefinedToEmptyString(isInDirection ? props.tcrStore!.entryWedgesNo.value : props.tcrStore!.exitWedgesNo.value)}`}
                </Text>
            </View>
        );
    }

    function renderActualTemperatureData(isInDirection: boolean) {
        return (
            <View style={equipmentAndDamageStyles.textCenterInCell}>
                <Text style={[styles.tableCellData, equipmentAndDamageStyles.tableCellText]}>
                    {`Temp 1: ${Utils.renderValueWithUnit(isInDirection ? props.tcrStore!.entryActualTemp1.value : props.tcrStore!.exitActualTemp1.value, "°C")}`}
                </Text>
                <Text style={[styles.tableCellData, equipmentAndDamageStyles.tableCellText]}>
                    {`Temp 2: ${Utils.renderValueWithUnit(isInDirection ? props.tcrStore!.entryActualTemp2.value : props.tcrStore!.exitActualTemp2.value, "°C")}`}
                </Text>
            </View>
        );
    }

    function equipmentAndDamagesTable(direction: Direction) {
        return (
            <View style={equipmentAndDamageStyles.textCenterInCell}>
                {props.tcrStore!.damages.filter(x => x.direction === direction).map((damage) => {
                    const sideType = TcrUtils.renderSideTypeForFile(damage.sideType);
                    const damageUpdateTime = TcrUtils.renderDamageUpdateTimeForFile(damage.damageUpdateTime!.toString());
                    const equipmentType = TcrUtils.renderEquipmentForFile(damage.damageLocation, damage.equipmentType);
                    const damageType = TcrUtils.renderDamageTypeForFile(damage.damageType);

                    return (
                        <View
                            key={`${damage.direction}-${damage.equipmentType}-${damage.damageType}`}
                        >
                            <Text style={[styles.tableCellData, equipmentAndDamageStyles.tableCellText, {lineHeight: 0}]}>
                                {`${sideType} | ${damageUpdateTime} | ${equipmentType} | ${damageType}`}
                            </Text>
                        </View>
                    );
                })}
            </View>
        );
    }
}